<template>
  <div class="question-wrapper">
    <div class="question-list">
      <div
        v-for="(question, index) in questions?.length"
        :key="index"
        class="question-circle"
        :class="questionSpeakingIndex === index ? 'question-active' : 'question-inactive'"
        @click="handleChangeQuestionSpeaking(index)"
      >
        {{ index + 1 }}
      </div>
    </div>
    <div v-html="question && question.content"></div>
    <div v-if="question" class="student-answer-wrapper">
      <span class="answer-label">Câu trả lời của học viên</span>
      <div v-if="question.answer" class="audio-controls">
        <audio
          ref="audioPlayer"
          controls
          preload="auto"
          class="audio-player"
        >
          <source :src="question.answer" :type="getAudioType(question.answer)">
          <source :src="question.answer" type="audio/weba">
          <source :src="question.answer" type="audio/webm">
          <source :src="question.answer" type="audio/ogg">
          <source :src="question.answer" type="audio/mpeg">
          Trình duyệt của bạn không hỗ trợ phần tử audio.
        </audio>
        <button
          type="button"
          class="speed-button"
          @click="cyclePlaybackRate"
        >
          {{ formatPlaybackRate(currentPlaybackRate) }}
        </button>
      </div>
      <span v-else class="no-audio">Không có bài làm của học viên</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpeakingQuestion',
  props: {
    questions: {
      type: Array,
      default: () => ({})
    },
    questionSpeakingIndex: {
      type: Number,
      required: true
    },
    question: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
      currentPlaybackRate: 1
    }
  },
  watch: {
    questionSpeakingIndex() {
      this.resetPlaybackRate()
    },
    'question.answer'() {
      this.resetPlaybackRate()
    }
  },
  mounted() {
    this.playCurrentAudio()
  },
  methods: {
    playCurrentAudio() {
      if (!this.autoplay || !this.question?.answer) return

      this.$nextTick(() => {
        const audio = this.$refs.audioPlayer
        if (!audio) return

        const startPlayback = () => {
          audio.currentTime = 0
          audio.muted = true
          audio.play()
            .then(() => {
              audio.muted = false
            })
            .catch(err => {
              console.warn('Auto-play failed:', err)
            })
        }

        if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          startPlayback()
        } else {
          audio.addEventListener('canplay', startPlayback, {once: true})
          audio.load()
        }
      })
    },
    applyPlaybackRate() {
      this.$nextTick(() => {
        if (this.$refs.audioPlayer) {
          this.$refs.audioPlayer.playbackRate = this.currentPlaybackRate
        }
      })
    },
    cyclePlaybackRate() {
      const currentIndex = this.playbackRates.indexOf(this.currentPlaybackRate)
      const nextIndex = (currentIndex + 1) % this.playbackRates.length

      this.currentPlaybackRate = this.playbackRates[nextIndex]
      this.applyPlaybackRate()
    },
    formatPlaybackRate(rate) {
      return `${rate}x`
    },
    getAudioType(audioUrl) {
      const url = Array.isArray(audioUrl) ? audioUrl[0] : audioUrl
      if (typeof url !== 'string') return ''
      const extension = url.split('.').pop()?.toLowerCase()
      switch (extension) {
        case 'weba':
          return 'audio/weba'
        case 'webm':
          return 'audio/webm'
        case 'ogg':
          return 'audio/ogg'
        case 'mp3':
          return 'audio/mpeg'
        default:
          return 'audio/weba'
      }
    },
    handleChangeQuestionSpeaking(index) {
      this.$emit('questionIndex', index)
    },
    resetPlaybackRate() {
      this.currentPlaybackRate = 1
      this.applyPlaybackRate()
      this.playCurrentAudio()
    }
  }
}
</script>

<style scoped>
.question-wrapper {
  border-radius: 6px;
  background-color: #f8f8f8;
  padding: 16px;
}

@media (min-width: 1024px) {
  .question-wrapper {
    padding: 1rem;
  }
}

.question-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
}

.question-circle {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.question-active {
  background-color: #0066ff;
  color: white;
}

.question-inactive {
  background-color: rgba(217, 217, 217, 0.4);
  color: black;
}

.student-answer-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.answer-label {
  font-weight: 600;
  margin-top: 8px;
  display: block;
}

.audio-controls {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
}

.audio-player {
  flex: 1;
  min-width: 0;
}

.speed-button {
  min-width: 60px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #ffffff;
  color: #111827;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.speed-button:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.speed-button:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.15);
}

@media (max-width: 640px) {
  .audio-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .speed-button {
    width: 100%;
  }
}

.no-audio {
  color: #ca8a04;
}
</style>
